import { ProductService } from './ProductService';
import { validateProductForm } from './validators/productValidators';
import { PRODUCT_MUTATIONS } from '@/core/services/store/modules/mutationTypes';

/**
 * Action utility class for product operations
 */
export class ProductActions {
  /**
   * Initialize product editor with required data
   * @param {Vue} component - Vue component with store access
   * @returns {Promise<boolean>} - Success flag
   */
  static async initializeProductEditor(component) {
    try {
      component.$store.commit(`product/${PRODUCT_MUTATIONS.SET_LOADING}`, true);

      const response = await component.initProductData();

      if (component.productId) {
        await component.fetchProductById(component.productId);
      }

      component.$store.commit(
        `product/${PRODUCT_MUTATIONS.SET_LOADING}`,
        false,
      );
      return response;
    } catch (error) {
      component.$store.commit(
        `product/${PRODUCT_MUTATIONS.SET_ERROR}`,
        error.message,
      );
      component.$store.commit(
        `product/${PRODUCT_MUTATIONS.SET_LOADING}`,
        false,
      );
      return null;
    }
  }

  /**
   * Handle category selection
   * @param {Vue} component - Vue component with store access
   * @param {Object} event - Selection event
   */
  static handleCategorySelect(component, event) {
    const categoryId = event.item.id;
    const categoryName = event.item.name;

    component.form.selectedCate = categoryName;
    component.$store.commit('product/SET_SELECTED_CATEGORY_ID', categoryId);
    component.$store.dispatch('product/updateProductInfo', {
      selectedCate: categoryName,
    });
  }

  /**
   * Handle category input change
   * @param {Vue} component - Vue component with store access
   * @param {String} text - Input text
   */
  static handleCategoryChange(component, text) {
    component.form.selectedCate = text;
    component.$store.dispatch('product/updateProductInfo', {
      selectedCate: text,
    });

    if (text === '') {
      component.$store.commit(
        'product/SET_FILTERED_CATEGORIES',
        component.$store.state.product.categories || [],
      );
      component.$store.commit('product/SET_SELECTED_CATEGORY_ID', null);
    } else {
      component.$store.dispatch('product/filterCategories', text);
    }
  }

  /**
   * Handle brand selection
   * @param {Vue} component - Vue component with store access
   * @param {Object} event - Selection event
   */
  static handleBrandSelect(component, event) {
    const brandId = event.item.id;
    const brandName = event.item.name;

    component.form.searchBrandName = brandName;
    component.$store.commit('product/SET_SELECTED_BRAND_ID', brandId);
    component.$store.dispatch('product/updateProductInfo', {
      searchBrandName: brandName,
    });
  }

  /**
   * Handle brand input change
   * @param {Vue} component - Vue component with store access
   * @param {String} text - Input text
   */
  static handleBrandChange(component, text) {
    component.form.searchBrandName = text;
    component.$store.dispatch('product/updateProductInfo', {
      searchBrandName: text,
    });

    if (text === '') {
      component.$store.commit(
        'product/SET_FILTERED_BRANDS',
        component.$store.state.product.brands || [],
      );
      component.$store.commit('product/SET_SELECTED_BRAND_ID', null);
    } else {
      component.$store.dispatch('product/filterBrands', text);
    }
  }

  /**
   * Handle parent product selection
   * @param {Vue} component - Vue component with store access
   * @param {Object} event - Selection event
   */
  static handleParentSelect(component, event) {
    const parentId = event.item.id;
    let parentName = event.item.name;

    if (event.item.code !== '') {
      parentName = event.item.code + ' - ' + event.item.name;
    }

    component.$store.commit('product/SET_SELECTED_PARENT_ID', parentId);
    component.$store.dispatch('product/updateProductInfo', {
      selectedParent: parentName,
    });

    // Generate child products (will regenerate as empty with parent product selected)
    this.generateChildProducts(component);
  }

  /**
   * Handle parent product input change
   * @param {Vue} component - Vue component with store access
   * @param {String} text - Input text
   */
  static handleParentChange(component, text) {
    component.$store.dispatch('product/updateProductInfo', {
      selectedParent: text,
    });
    component.$store.dispatch('product/searchProducts', text);

    // Generate child products
    this.generateChildProducts(component);
  }

  /**
   * Generate child products based on options and form data
   * @param {Vue} component - Vue component with store access
   */
  static generateChildProducts(component) {
    component.$store.dispatch('product/generateChildProducts', {
      isGenChild: component.isGenChild || false,
    });
  }

  /**
   * Create new brand
   * @param {Vue} component - Vue component with store access
   * @param {String} brandName - New brand name
   * @returns {Promise<boolean>} - Success flag
   */
  static async createBrand(component, brandName) {
    console.log('brandName', brandName);
    if (!brandName) {
      return false;
    }

    try {
      const result = await component.$store.dispatch('product/createBrand', {
        id: component.$uuid.v4(),
        name: brandName,
      });

      component.showBrandModal = false;

      if (result.status === 1) {
        if (component.makeToastSuccess) {
          component.makeToastSuccess(result.message);
        }
        return true;
      } else {
        if (component.makeToastFailure) {
          component.makeToastFailure(result.message);
        }
        return false;
      }
    } catch (error) {
      if (component.makeToastFailure) {
        component.makeToastFailure('Lỗi khi tạo thương hiệu: ' + error.message);
      }
      return false;
    }
  }

  /**
   * Create property status value
   * @param {Vue} component - Vue component with store access
   * @param {String} statusName - Status name
   * @param {String} statusCode - Status code
   * @param {Number} propertyTypeId - Property type ID
   * @returns {Promise<boolean>} - Success flag
   */
  static async createPropertyStatus(
    component,
    statusName,
    statusCode,
    propertyTypeId,
  ) {
    if (!statusName || !statusCode) {
      return false;
    }

    try {
      const result = await component.$store.dispatch(
        'product/createPropertyValue',
        {
          propertyData: {
            name: statusName,
            code: statusCode,
            productPropertyId: component.productPropertyId,
          },
          typeId: propertyTypeId,
        },
      );

      if (result.status === 1) {
        component.showStatusModal = false;

        if (component.makeToastSuccess) {
          component.makeToastSuccess(result.message);
        }
        return true;
      } else {
        if (component.makeToastFailure) {
          component.makeToastFailure(result.message);
        }
        return false;
      }
    } catch (error) {
      if (component.makeToastFailure) {
        component.makeToastFailure('Lỗi khi tạo thuộc tính: ' + error.message);
      }
      return false;
    }
  }

  /**
   * Create child product
   * @param {Vue} component - Vue component with store access
   * @returns {boolean} - Success flag
   */
  static createChildProduct(component) {
    try {
      // Get color and size info
      let colorName = '';
      let colorCode = '';
      let sizeName = '';
      let sizeCode = '';

      // Find color info
      component.colors.forEach((element) => {
        if (element.id === component.selectedColorChild) {
          colorName = element.name;
          colorCode = element.code;
        }
      });

      // Find size info
      component.sizes.forEach((element) => {
        if (element.id === component.selectedSizeChild) {
          sizeName = element.name;
          sizeCode = element.code;
        }
      });

      // Create child product data
      const childProduct = {
        id: component.form.code + '-' + colorCode + '-' + sizeCode,
        count:
          component.childProducts.length > 0
            ? component.childProducts[component.childProducts.length - 1]
                .count + 1
            : 1,
        productName: component.form.name + ' - ' + colorName + ' - ' + sizeName,
        productCode: component.form.code + '-' + colorCode + '-' + sizeCode,
        barCode: component.childBarCode,
        originalPrice: component.childOriginalPrice,
        sellingPrice: component.childSalePrice,
        totalQualityInstock: 0,
        color: component.selectedColorChild,
        size: component.selectedSizeChild,
        status: component.$store.state.product.productInfo.selectedStatus,
      };

      // Add to store
      component.$store.dispatch('product/addChildProduct', childProduct);
      component.hideAddProductModal();
      return true;
    } catch (error) {
      console.error('Error creating child product:', error);
      if (component.makeToastFailure) {
        component.makeToastFailure(
          'Lỗi khi tạo sản phẩm con: ' + error.message,
        );
      }
      return false;
    }
  }

  /**
   * Update child product
   * @param {Vue} component - Vue component with store access
   * @returns {boolean} - Success flag
   */
  static updateChildProduct(component) {
    try {
      component.$store.dispatch('product/updateChildProduct', {
        id: component.childId,
        updates: {
          productCode: component.childCode,
          productName: component.childName,
          barCode: component.childBarCode,
          originalPrice: component.childOriginalPrice,
          sellingPrice: component.childSalePrice,
        },
      });

      component.hideUpdateProductModal();
      return true;
    } catch (error) {
      console.error('Error updating child product:', error);
      if (component.makeToastFailure) {
        component.makeToastFailure(
          'Lỗi khi cập nhật sản phẩm con: ' + error.message,
        );
      }
      return false;
    }
  }

  /**
   * Add product to combo
   * @param {Vue} component - Vue component with store access
   * @returns {boolean} - Success flag
   */
  static addProductToCombo(component) {
    try {
      const comboProduct = {
        id: component.selectedProductForCombo.id,
        productCode: component.selectedProductForCombo.code,
        productName: component.selectedProductForCombo.name,
        quantity: component.proComboQuantity || 1,
        productComboPrice: component.productComboPrice,
        productComboRate: component.productComboRate,
        isEdit: false,
      };

      // Check for duplicates
      const isDuplicate = component.comboProducts.some(
        (product) => product.id === comboProduct.id,
      );
      if (isDuplicate) {
        if (component.makeToastFailure) {
          component.makeToastFailure('Sản phẩm trùng!');
        }
        return false;
      }

      component.$store.dispatch('product/addComboProduct', comboProduct);
      component.hideComboProductModal();
      return true;
    } catch (error) {
      console.error('Error adding product to combo:', error);
      if (component.makeToastFailure) {
        component.makeToastFailure(
          'Lỗi khi thêm sản phẩm vào combo: ' + error.message,
        );
      }
      return false;
    }
  }

  /**
   * Save product to database
   * @param {Vue} component - Vue component with store access
   * @returns {Promise<boolean>} - Success flag
   */
  static async saveProduct(component) {
    // Validate form
    const validation = validateProductForm(component.form);
    if (!validation.isValid) {
      // Touch fields for UI validation (vuelidate)
      if (component.$v && component.$v.form) {
        component.$v.form.$touch();
      }
      return false;
    }

    // Check required selections
    if (!component.$store.state.product.selectedCategoryId) {
      if (component.makeToastFailure) {
        component.makeToastFailure('Vui lòng chọn danh mục sản phẩm');
      }
      return false;
    }

    if (!component.$store.state.product.selectedBrandId) {
      if (component.makeToastFailure) {
        component.makeToastFailure('Vui lòng chọn thương hiệu');
      }
      return false;
    }

    // For combo products, check if sum matches selling price
    if (component.$store.state.product.productInfo.selectedType === 3) {
      const sumComboPrice = await component.$store.dispatch(
        'product/calculateComboSum',
      );
      const sellingPrice = ProductService.unMaskPrice(
        component.$store.state.product.productInfo.salePrice,
      );

      if (sumComboPrice !== sellingPrice) {
        if (component.makeToastFailure) {
          component.makeToastFailure(
            'Giá bán lẻ không bằng tổng giá sản phẩm combo!',
          );
        }
        return false;
      }
    }

    try {
      const result = await component.$store.dispatch('product/saveProduct', {
        form: component.form,
        uuid: component.$uuid.v4,
      });

      if (result.status === 1) {
        if (component.makeToastSuccess) {
          component.makeToastSuccess(result.message);
        }

        // Navigate to product list after save
        setTimeout(() => {
          component.$router.push({ name: 'list-products' });
        }, component.TIME_TRIGGER || 2000);

        return true;
      } else {
        if (component.makeToastFailure) {
          component.makeToastFailure(result.message);
        }
        return false;
      }
    } catch (error) {
      console.error('Error saving product:', error);
      if (component.makeToastFailure) {
        component.makeToastFailure('Lỗi khi lưu sản phẩm: ' + error.message);
      }
      return false;
    }
  }
}
